import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Order as Entity } from './order.model';
import { OrderActions as Actions } from './order.actions';
import { OrderSelectors as Selectors } from './order.selectors';
import { AppState } from '../app-store.state';
import { generateUniqueId } from '../app-store.functions';
import { IPage, IResultWithPageInfo } from '../app-store.interfaces';

@Injectable({ providedIn: 'root' })
export class OrderFacade {
    all$: Observable<Entity[]> = this.store.pipe(select(Selectors.selectAll));
    isLoading$: Observable<boolean> = this.store.pipe(select(Selectors.isLoading));
    hasError$: Observable<Error | undefined> = this.store.pipe(select(Selectors.hasErrors));
    current$: Observable<Entity | undefined> = this.store.pipe(select(Selectors.currentEntity));
    totalPageable$: Observable<number> = this.store.pipe(select(Selectors.totalPageable));
    currentPage$: Observable<IPage> = this.store.pipe(select(Selectors.currentPage));

    constructor(private store: Store<AppState>) {}

    loadPage(pageNumber: number, pageSize: number): void {
        this.store.dispatch(Actions.loadPage({ page: { pageNumber, pageSize } }));
    }

    loadPageWithResult(pageNumber: number, pageSize: number): Observable<IResultWithPageInfo<Entity>> {
        const correlationId = generateUniqueId();
        this.store.dispatch(Actions.loadPage({ page: { pageNumber, pageSize }, correlationId }));
        return this.store
            .select(Selectors.findByCorrelationId<IResultWithPageInfo<Entity>>(correlationId))
            .pipe(filter((x) => !!x));
    }

    loadSingle(entityId: number): void {
        this.store.dispatch(Actions.loadSingle({ entityId }));
    }

    loadSingleWithResult(entityId: number): Observable<Entity> {
        const correlationId = generateUniqueId();
        this.store.dispatch(Actions.loadSingle({ entityId, correlationId }));
        return this.store
            .select(Selectors.findByCorrelationId<Entity>(correlationId))
            .pipe(filter((x) => !!x));
    }

    setCurrentId(entityId: number): void {
        this.store.dispatch(Actions.setCurrentEntityId({ entityId }));
    }

    deselectCurrent(): void {
        this.store.dispatch(Actions.deselectCurrent());
    }

    getById(entityId: number): Observable<Entity | undefined> {
        return this.store.pipe(select(Selectors.selectById(entityId)));
    }
}
